/**
 * "Quer receber alertas?" — perguntado UMA vez por conta neste aparelho, na
 * primeira abertura depois de entrar (teste físico, item 18).
 *
 * O navegador só abre o pedido de permissão a partir de um toque, então não
 * dá para "pedir sozinho ao abrir": o que dá é perguntar com uma folha e
 * deixar o toque em "Ativar alertas" disparar o pedido do sistema. Com a
 * permissão dada, os alertas ficam ligados — esse é o padrão que o PO quer.
 * "Agora não" guarda a resposta e o Perfil continua tendo o interruptor.
 *
 * Só aparece com o app INSTALADO: numa aba de navegador, pedir notificação
 * ao abrir é o que site faz, e o Perfil continua tendo o interruptor. Também
 * não aparece com permissão já negada nem com o servidor sem chave.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useAuthStore } from '../../auth/store';
import { isStandalone } from '../../install/detect';
import { Sheet } from '../../ui/Sheet';
import { estadoDosAlertas, ligarAlertas } from './push';

// Fora do prefixo varrido no logout: sair e entrar de novo não repete a pergunta.
const CHAVE = (userId: string) => `nv:alertas:perguntado:${userId}`;

async function lerConfig(): Promise<{ push: { publicKey: string } | null }> {
  const res = await fetch('/api/v1/config', { credentials: 'same-origin' });
  if (!res.ok) return { push: null };
  return (await res.json()) as { push: { publicKey: string } | null };
}

function jaPerguntou(userId: string): boolean {
  try {
    return localStorage.getItem(CHAVE(userId)) === 'sim';
  } catch {
    return true;
  }
}

function lembrarPergunta(userId: string): void {
  try {
    localStorage.setItem(CHAVE(userId), 'sim');
  } catch {
    /* sem storage: pergunta de novo na próxima, sem drama */
  }
}

export function PedidoDeAlertas() {
  const userId = useAuthStore((s) => s.user?.id ?? '');
  const config = useQuery({ queryKey: ['config'], queryFn: lerConfig, staleTime: Infinity });
  const [aberto, setAberto] = useState(false);
  const [ligando, setLigando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const publicKey = config.data?.push?.publicKey ?? null;

  useEffect(() => {
    if (!userId || !publicKey || jaPerguntou(userId)) return;
    if (!isStandalone(window.matchMedia, (navigator as { standalone?: boolean }).standalone)) return;
    let vivo = true;
    void estadoDosAlertas()
      .then((estado) => {
        if (vivo && estado === 'desligado') setAberto(true);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [userId, publicKey]);

  if (!aberto || !publicKey) return null;

  const fechar = () => {
    lembrarPergunta(userId);
    setAberto(false);
  };

  async function ativar() {
    if (!publicKey) return;
    setLigando(true);
    setErro(null);
    try {
      await ligarAlertas(publicKey);
      fechar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível ligar os alertas.');
    } finally {
      setLigando(false);
    }
  }

  return (
    <Sheet titleId="alertas-titulo" onFechar={fechar} testId="pedido-de-alertas">
      <h2 className="sheet__title" id="alertas-titulo">Receber alertas da viagem?</h2>
      <p className="sheet__text">
        Aviso antes das atividades com alerta, recados de quem organiza e o ponto de encontro. O telefone vai pedir sua permissão.
      </p>
      {erro && <p className="auth-error" role="alert">{erro}</p>}
      <button type="button" className="nv-btn nv-btn--primary nv-btn--block" disabled={ligando} onClick={() => void ativar()} data-testid="pedido-de-alertas-sim">
        {ligando ? 'Ativando…' : 'Ativar alertas'}
      </button>
      <button type="button" className="nv-btn nv-btn--secondary nv-btn--block" onClick={fechar}>
        Agora não
      </button>
    </Sheet>
  );
}
